import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { checkCockpitAdmin } from "../../lib/admin";
import { getProfile, type Profile } from "../../lib/profiles";
import CockpitSidebar from "../../components/cockpit/CockpitSidebar";

export default function CockpitLayout() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"checking" | "ok">("checking");
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session?.user?.id) {
        navigate("/", { replace: true });
        return;
      }
      const ok = await checkCockpitAdmin(session.user.id);
      if (cancelled) return;
      if (!ok) {
        navigate("/", { replace: true });
        return;
      }
      const p = await getProfile(session.user.id).catch(() => null);
      if (cancelled) return;
      setProfile(p);
      setPhase("ok");
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (phase === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <Loader2 className="h-10 w-10 animate-spin text-orange-500" strokeWidth={1.5} />
      </div>
    );
  }

  const displayName =
    [profile?.prenom, profile?.nom].filter(Boolean).join(" ").trim() || "Admin";
  const avatarUrl = profile?.avatar_url?.trim() || null;

  return (
    <div className="flex min-h-screen bg-slate-900 text-slate-100">
      <CockpitSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-800 bg-slate-950/50 px-6 backdrop-blur-sm">
          <h1 className="text-sm font-semibold tracking-tight text-white">Cockpit Woahda</h1>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-400 sm:inline">{displayName}</span>
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="h-9 w-9 rounded-full border border-slate-700 object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 bg-slate-800 text-xs font-semibold text-orange-400">
                {displayName.slice(0, 2).toUpperCase()}
              </div>
            )}
            <button
              type="button"
              onClick={() => supabase.auth.signOut().then(() => navigate("/"))}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
              title="Déconnexion"
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
